import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * Asks the browser to confirm leaving or reloading while any save is still in flight.
 * Timesheet saves are queued one at a time, so a quick reload could otherwise drop edits.
 *
 * Follows the mutation cache directly instead of `useIsMutating()`, so the component using it
 * (the app shell) doesn't re-render whenever a save starts or ends.
 */
export function useLeaveWarning() {
  const qc = useQueryClient();
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    let warning = false;
    const sync = () => {
      const saving = qc.isMutating() > 0;
      if (saving === warning) return;
      warning = saving;
      if (saving) window.addEventListener('beforeunload', onBeforeUnload);
      else window.removeEventListener('beforeunload', onBeforeUnload);
    };
    sync();
    const unsubscribe = qc.getMutationCache().subscribe(sync);
    return () => {
      unsubscribe();
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [qc]);
}
